/**
 * DoneForDayModal — end-of-day clock-out wizard.
 *
 * Captures: hours worked (15-min spinner), lunch Yes/No (default comes from
 * Timekeep Settings), required notes, AND now a per-material progress
 * checkbox section so techs can mark which BOM rows they finished today.
 * Material checkboxes write to `wo.dispatch_card_progress` via
 * /api/work-orders/{wo_id}/card-progress/check (and /uncheck) so the
 * green-tick state shows up immediately on every WO Card surface
 * (Open WO sidebar, Dispatch board cell, Tech /field card). Strictly
 * scoped — never touches Bill of Materials, Procurement Log, Budget
 * tab, or invoice line items.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Minus, Plus, Check } from "lucide-react";
import {
  getTodaysTimesheet,
  submitTechEntry,
  checkCardProgress,
  uncheckCardProgress,
  addContractFieldNote,
} from "@/lib/api";
import { progressKeyFor, isPartChecked } from "@/lib/cardProgress";
import { useI18n, tr } from "@/i18n";
import { Tx } from "@/i18n/Tx";

export default function DoneForDayModal({
  open,
  onOpenChange,
  techId,
  techName,
  woId,
  parts,
  coParts,
  progressKeys,
  onProgressChange,
  onSaved,
  // iter396 — Install / Overhead / Warranty: the note also lands on the job's Field Notes.
  contractJobId,
  jobName,
}) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState(10);
  const [tookLunch, setTookLunch] = useState(true);
  const [lunchMinutes, setLunchMinutes] = useState(30);
  const [injuryReported, setInjuryReported] = useState(false);
  const [notes, setNotes] = useState("");
  const [timesheetId, setTimesheetId] = useState(null);
  const [gpsHours, setGpsHours] = useState(0);
  // Local mirror of the WO's dispatch_card_progress so the checkboxes can
  // optimistically toggle without waiting on a parent refresh.
  const [progress, setProgress] = useState([]);

  // Pull today's timesheet + Timekeep Settings defaults whenever the modal
  // opens. The spinner seeds to `default_hours`, lunch toggle to
  // `lunch_default_yes` — exactly per the admin's Timekeep Settings.
  useEffect(() => {
    if (!open || !techId) return;
    setProgress(progressKeys || []);
    (async () => {
      try {
        const r = await getTodaysTimesheet(techId);
        setTimesheetId(r.data.timesheet?.id || null);
        setGpsHours(r.data.timesheet?.gps_hours || 0);
        const d = r.data.defaults || {};
        setHours(d.default_hours ?? 10);
        setLunchMinutes(d.default_lunch_minutes ?? 30);
        setTookLunch(d.lunch_default_yes !== false);
      } catch {
        toast.error(tr("Could not load today's timesheet"));
      }
    })();
  }, [open, techId, progressKeys]);

  const bump = (delta) => setHours(h => Math.max(0, Math.round((h + delta) * 4) / 4));

  // Materials list: base WO parts + any flattened CO parts. De-duped by key.
  const allParts = (() => {
    const out = [];
    const seen = new Set();
    for (const p of (parts || [])) {
      const k = progressKeyFor(p);
      if (!k || seen.has(k)) continue;
      seen.add(k); out.push({ ...p, _key: k, _from: "wo" });
    }
    for (const p of (coParts || [])) {
      const k = progressKeyFor(p);
      if (!k || seen.has(k)) continue;
      seen.add(k); out.push({ ...p, _key: k, _from: "co" });
    }
    return out;
  })();

  const toggleMaterial = async (part) => {
    if (!woId) { toast.error(t("WO missing")); return; }
    const key = part._key;
    const checked = isPartChecked(part, progress);
    // Optimistic update
    const next = checked
      ? progress.filter(p => (p?.key || "").toLowerCase() !== key)
      : [...progress, { key, checked_at: new Date().toISOString(), checked_by: techName || "Tech" }];
    setProgress(next);
    try {
      if (checked) {
        await uncheckCardProgress(woId, { key });
      } else {
        await checkCardProgress(woId, { key, checked_by: techName || "Tech" });
      }
      if (onProgressChange) onProgressChange(next);
    } catch (e) {
      // Roll back on failure
      setProgress(progress);
      toast.error(e.response?.data?.detail || t("Could not update progress"));
    }
  };

  const save = async () => {
    if (!timesheetId) { toast.error(t("No timesheet loaded")); return; }
    if (!notes.trim()) { toast.error(t("Notes are required")); return; }
    setLoading(true);
    try {
      const text = notes.trim();
      const r = await submitTechEntry(timesheetId, {
        tech_entered_hours: hours,
        took_lunch: tookLunch,
        lunch_minutes: tookLunch ? lunchMinutes : 0,
        injury_reported: injuryReported,
        notes: jobName ? `${jobName}: ${text}` : text,
        append_notes: true,
      });
      if (contractJobId) {
        try { await addContractFieldNote(contractJobId, { text, tech_id: techId, tech_name: techName }); }
        catch { toast.warning(t("Timesheet saved, but the note could not be added to the job")); }
      }
      if (r.data.flagged_reason) {
        toast.warning(t("Flagged for PM review: {reason}", { reason: r.data.flagged_reason }));
      } else {
        toast.success(t("Timesheet saved"));
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Save failed"));
    } finally { setLoading(false); }
  };

  const checkedCount = progress.filter(p => p?.key).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] max-h-[90vh] overflow-y-auto" data-testid="done-for-day-modal">
        <DialogHeader>
          <DialogTitle>{t("Done for Day")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {gpsHours > 0 && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded px-3 py-2">
              {t("GPS recorded {h}h on the clock today.", { h: gpsHours })}
            </div>
          )}

          <div>
            <Label className="text-xs">{t("Hours Worked")}</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button type="button" variant="outline" size="sm" className="h-10 w-10 p-0" onClick={() => bump(-0.25)} data-testid="btn-hours-down">
                <Minus className="w-4 h-4" />
              </Button>
              <div className="flex-1 text-center">
                <div className="text-3xl font-bold tabular-nums" data-testid="display-hours">{hours.toFixed(2)}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wide">{t("15-min steps")}</div>
              </div>
              <Button type="button" variant="outline" size="sm" className="h-10 w-10 p-0" onClick={() => bump(0.25)} data-testid="btn-hours-up">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs">{t("Did you take lunch?")}</Label>
            <div className="flex items-center gap-2 mt-1" data-testid="lunch-buttons">
              <Button type="button" variant={!tookLunch ? "default" : "outline"} size="sm" className="flex-1 h-9" onClick={() => { setTookLunch(false); }} data-testid="btn-lunch-no">
                {t("No")}
              </Button>
              <Button type="button" variant={tookLunch && lunchMinutes === 30 ? "default" : "outline"} size="sm" className="flex-1 h-9" onClick={() => { setTookLunch(true); setLunchMinutes(30); }} data-testid="btn-lunch-30">
                30 min
              </Button>
              <Button type="button" variant={tookLunch && lunchMinutes === 60 ? "default" : "outline"} size="sm" className="flex-1 h-9" onClick={() => { setTookLunch(true); setLunchMinutes(60); }} data-testid="btn-lunch-60">
                60 min
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">{t("GPS will validate against stationary windows. A mismatch flags this timesheet for PM review.")}</p>
          </div>

          <div>
            <Label className="text-xs">{t("Any injury today?")}</Label>
            <div className="flex items-center gap-2 mt-1" data-testid="injury-buttons">
              <Button type="button" variant={!injuryReported ? "default" : "outline"} size="sm" className="flex-1 h-9" onClick={() => setInjuryReported(false)} data-testid="btn-injury-no">
                {t("No")}
              </Button>
              <Button type="button" variant={injuryReported ? "default" : "outline"} size="sm" className={`flex-1 h-9 ${injuryReported ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`} onClick={() => setInjuryReported(true)} data-testid="btn-injury-yes">
                {t("Yes — describe in notes")}
              </Button>
            </div>
          </div>

          {/* Per-material progress checklist. Tech checks off whichever
              rows they finished today; the green-tick state syncs across
              all 3 WO card surfaces immediately. */}
          {allParts.length > 0 && (
            <div data-testid="material-progress-section">
              <div className="flex items-center justify-between">
                <Label className="text-xs">{t("Materials Installed Today")}</Label>
                <span className="text-[10px] text-slate-400">{t("{a}/{b} done", { a: checkedCount, b: allParts.length })}</span>
              </div>
              <div className="mt-1 space-y-1 max-h-[200px] overflow-y-auto rounded border border-slate-200">
                {allParts.map((p) => {
                  const checked = isPartChecked(p, progress);
                  return (
                    <button
                      key={p._key}
                      type="button"
                      onClick={() => toggleMaterial(p)}
                      data-testid={`material-progress-toggle-${p._key}`}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs border-b border-slate-100 last:border-b-0 ${checked ? "bg-emerald-50" : "bg-white hover:bg-slate-50"}`}
                    >
                      <span className={`flex items-center justify-center w-4 h-4 rounded border ${checked ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 bg-white"}`}>
                        {checked && <Check className="w-3 h-3" />}
                      </span>
                      <span className={`flex-1 min-w-0 break-words ${checked ? "text-emerald-800 font-medium" : "text-slate-700"}`}>
                        {Math.round(p.quantity || 1)}x <Tx text={p.description} />
                        {p.part_number ? <span className="text-slate-400 ml-1">[{p.part_number}]</span> : null}
                        {p._from === "co" && <span className="ml-1 text-purple-600 text-[10px]">(CO)</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">{t("Notes (required)")}</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
              placeholder={t("Brief summary of the day's work...")}
              data-testid="input-done-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-done-cancel">{t("Cancel")}</Button>
          <Button onClick={save} disabled={loading || !notes.trim()} className="bg-slate-600 hover:bg-slate-700 text-white" data-testid="btn-done-save">
            {loading ? t("Saving...") : t("Save & Clock Out")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
